/**
 * Centralized query options factories for React Query
 * These can be used directly in hooks or for prefetching
 */

import type { InfiniteData } from "@tanstack/react-query";
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
} from "@tanstack/react-query";
import type { FilterResultPage, FilterSearchParams } from "../../types/filter";
import type { GeoApifyPayload } from "../../utils/geoapify/geoapify";
import { validateSubclientString } from "../../utils/validators/filter-search-params";
import { filterApi, geoApi, hybridApi } from "./api";

/**
 * Hooks pass `useClient()`, which is the app's own client (`env.CLIENT`) on
 * the main board, while the server prefetch and the API routes treat that as
 * "no subclient". Normalizing here keeps one key per board, so the hooks read
 * what the server prefetched instead of missing it and fetching again (during
 * SSR that miss is a loopback over HTTP, and the job list renders empty).
 */
const toSubclient = (client: string | undefined) =>
  validateSubclientString.parse(client);

const withSubclient = (
  filterSearchParams: FilterSearchParams
): FilterSearchParams => ({
  ...filterSearchParams,
  subclient: toSubclient(filterSearchParams.subclient),
});

// Filter query options
export const filterQueryOptions = {
  /**
   * Query options for filter info
   */
  info: (locale: string, client?: string) => {
    const subclient = toSubclient(client);
    return queryOptions({
      queryKey: ["filter", "info", { locale, subclient }],
      queryFn: () => filterApi.fetchInfo(locale, subclient),
      gcTime: Number.POSITIVE_INFINITY,
      staleTime: Number.POSITIVE_INFINITY,
    });
  },

  /**
   * Infinite query options for job search
   */
  search: (
    locale: string,
    searchParams: FilterSearchParams,
    options?: {
      enabled?: boolean;
      refetch?: boolean;
      placeholderData?:
        | ((
            previousData: InfiniteData<FilterResultPage, unknown> | undefined
          ) => InfiniteData<FilterResultPage, unknown> | undefined)
        | undefined;
    }
  ) => {
    const filterSearchParams = withSubclient(searchParams);
    return infiniteQueryOptions({
      queryKey: ["joblist", locale, filterSearchParams],
      queryFn: ({ pageParam }) =>
        filterApi.fetchJobList(locale, filterSearchParams, pageParam as number),
      initialPageParam: 0,
      getNextPageParam: (lastPage) => lastPage.nextPage || undefined,
      getPreviousPageParam: (firstPage) => firstPage.previousPage || undefined,
      staleTime:
        options?.refetch === false ? Number.POSITIVE_INFINITY : 1000 * 60 * 15,
      gcTime:
        options?.refetch === false ? Number.POSITIVE_INFINITY : 1000 * 60 * 30,
      refetchOnWindowFocus: options?.refetch !== false,
      enabled: options?.enabled ?? true,
      retry: 3,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30_000),
      placeholderData: options?.placeholderData,
    });
  },

  /**
   * Query options for map pins
   */
  mapPins: (
    locale: string,
    searchParams: FilterSearchParams,
    options?: {
      enabled?: boolean;
      refetch?: boolean;
    }
  ) => {
    const filterSearchParams = withSubclient(searchParams);
    return queryOptions({
      queryKey: ["jobMapPins", locale, filterSearchParams],
      queryFn: () => filterApi.fetchMapPins(locale, filterSearchParams),
      staleTime:
        options?.refetch === false ? Number.POSITIVE_INFINITY : 1000 * 60 * 15,
      gcTime:
        options?.refetch === false ? Number.POSITIVE_INFINITY : 1000 * 60 * 5,
      refetchOnWindowFocus: options?.refetch !== false,
      enabled: options?.enabled ?? true,
    });
  },
};

// Hybrid Search query options (Skill Matcher + Typesense Filters)
export const hybridQueryOptions = {
  questionSearch: (
    locale: string,
    searchQuestion: string,
    rerankInstruction: string,
    filterSearchParams: Omit<
      FilterSearchParams,
      "postingDateFilter" | "subclient"
    >,
    options?: {
      enabled?: boolean;
    }
  ) =>
    infiniteQueryOptions({
      queryKey: [
        "hybrid-search-question",
        locale,
        searchQuestion,
        rerankInstruction,
        filterSearchParams,
      ],
      queryFn: ({ pageParam, signal }) =>
        hybridApi.fetchQuestionResults(
          locale,
          searchQuestion,
          rerankInstruction,
          filterSearchParams,
          pageParam as number,
          signal
        ),
      initialPageParam: 0,
      getNextPageParam: (lastPage) => lastPage.nextPage || undefined,
      getPreviousPageParam: (firstPage) => firstPage.previousPage || undefined,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      enabled: options?.enabled ?? true,
      // 2026-09-15: a key change (filter, suggestion, confirm in penalty
      // mode) used to replace the whole list with the skeleton and throw
      // the reader to the top. Keep the last page on screen, dimmed via
      // `isPlaceholderData`, until the new one lands.
      placeholderData: keepPreviousData,
      // One retry, only for a server-side or network failure. A 4xx (429
      // budget, 400 validation) cannot succeed on repeat, and this is an
      // ~8 s AI pipeline — the old two near-immediate retries compounded
      // exactly the transient Voyage throttling they were meant to ride out.
      retry: (failureCount, error) => {
        const status = (error as Error & { status?: number }).status;
        if (status !== undefined && status < 500) {
          return false;
        }
        return failureCount < 1;
      },
      retryDelay: () => 2000,
    }),
};

// Geo query options
export const geoQueryOptions = {
  /**
   * Query options for geo search
   */
  search: <TPayload extends GeoApifyPayload>(
    payload: TPayload,
    enabled?: boolean
  ) =>
    queryOptions({
      queryKey: Object.values(payload).filter(Boolean),
      queryFn: () => geoApi.fetchGeoLocation(payload),
      enabled: enabled ?? true,
      staleTime: 1000 * 60 * 15,
      gcTime: 1000 * 60 * 15,
    }),

  /**
   * Query options for boundaries
   */
  boundaries: (
    placeId: string | undefined,
    locale: string,
    enabled?: boolean
  ) =>
    queryOptions({
      queryKey: ["geo-boundaries", placeId, locale],
      queryFn: () => {
        if (!placeId) {
          throw new Error("No placeId provided");
        }
        return geoApi.fetchBoundaries(placeId, locale);
      },
      enabled: Boolean(placeId && enabled),
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
    }),
};
